import { BreakpointObserver } from '@angular/cdk/layout';
import { Platform } from '@angular/cdk/platform';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDrawerToggleResult, MatSidenav } from '@angular/material/sidenav';
import { NavigationEnd, NavigationStart, Router,Event as RouterEvent } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { filter, map, withLatestFrom } from 'rxjs/operators';
import { SessionStorageService } from 'src/app/shared/services/session-storage.service';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from './core/services/authentication/authentication.service';
import { SessionManagementService } from 'src/app/core/services/session/session-management.service';
import { Constants } from './shared/constants';
import { IsAuthorizedDirective } from './shared/directives/is-authorized.directive';
import { BroadcastService } from './shared/services/broadcast.service';
import { LandingRedirectService } from './shared/services/landing-redirect.service';
import { LocalStorageService } from './shared/services/local-storage.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    standalone: false
})
export class AppComponent implements OnInit {
  subscriptionList: Subscription;
  title = 'LabPort';
  idleState = 'Not started.';
  timedOut = false;
  lastPing?: Date = null;
  isScreenSmall: Observable<boolean>;
  public isSidenavOpened = false;
  isLoad = true;
  ishideScrollState = false;
  isManageUserActive = false;
  public subscriptions: Subscription[] = [];
  isAuthenticated: boolean;
  dialogRef: any;
  hasAuthorisationControl: boolean;
  scollToHide: Array<string>;
  isVerificationSuccessful: boolean;
  isMfaEnabled: boolean;
  @ViewChild(MatSidenav) sidenav: MatSidenav;
  @ViewChild(IsAuthorizedDirective) directive;

  constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private landingService: LandingRedirectService,
    breakpoints: BreakpointObserver, private broadcastService: BroadcastService,
    private session: SessionManagementService,
    public platform: Platform,
    public sessionStorage: SessionStorageService,) {
    this.session.setupSessionLogoutSnackbar();
    this.isScreenSmall = breakpoints.observe(`(max-width: ${Constants.SMALL_WIDTH_BREAKPOINT}px)`)
      .pipe(map(breakpoint => breakpoint.matches));

    router.events.pipe(
      withLatestFrom(this.isScreenSmall),
      filter(([a, b]) => b && a instanceof NavigationEnd)
    ).subscribe(_ => this.sidenav.close());

    router.events.subscribe((event: RouterEvent) => {
      if (this.isLoad && event instanceof NavigationStart && event.url !== '/account/login'
        && this.authenticationService.GetClaims() !== null) {
        this.handleNavigationStart(event);
      }
      if (event instanceof NavigationEnd) {
        this.handleNavigationEnd(event);
      }
    });
    if (!this.authenticationService.GetBasicAuthToken()) {
      this.authenticationService.SetNGetBasicTokenFromSession((data) => {
        // Decrypt a basic token from session storage and assign it in broadCast service variable.
      });
   }
  }

  private handleNavigationStart(event: NavigationStart) {
    const isAnnoucementShown = JSON.parse(this.sessionStorage.getItem('isAnnoucementShown'));
    if (event.url !== Constants.HOME_TAB || isAnnoucementShown) {
      this.authenticationService.checkPasswordExpiryStatus();
    } else {
      this.showAnnoncementModal();
    }
    this.isLoad = false;
    const URL = event.url.split('/').pop();
    if (URL !== 'results' && URL !== 'manage-customer-users' && URL !== '' && URL !== 'specimen-type'
      && URL !== 'tests' && URL !== 'test-panels' && URL !== 'questions' && URL !== 'order' && URL !== 'payors') {
      return;
    } else {
      this.navigationFunc(URL);
    }
  }

  private handleNavigationEnd(event: NavigationEnd) {
    window.scrollTo(0, 0);
    this.isAuthenticated = this.authenticationService.IsAuthenticated();
    this.isVerificationSuccessful = this.localStorageService.getItem(Constants.IS_VERIFICATION_SUCCESSFUL) === 'true';
    const loggedInUserInfoJson = JSON.parse(this.localStorageService.getItem(Constants.LOGGED_IN_USER_INFO));
    this.isMfaEnabled = loggedInUserInfoJson != null ? loggedInUserInfoJson.isMfaEnabled : '';
    this.scollToHide = ['order', 'announcements'];
    this.ishideScrollState = true;
    this.isLoad = true;
    this.updateBodyScrollState(event.url);
    this.broadcastService.setLinkUserActive({ url: event.url });
    this.hasAuthorisationControl = this.authenticationService.GetAuthorisedControlList();
  }

  private updateBodyScrollState(nextURL: string) {
    const isAccountRoute = nextURL === '/account' || nextURL.startsWith('/account/');
    if (isAccountRoute) {
      this.ishideScrollState = true;
      if (!this.platform.TRIDENT) {
        document.querySelector('body').classList.remove('hideBodyScroll');
      }
      return;
    }

    if (nextURL.indexOf('results') > -1 ||
      nextURL.indexOf('manage-customer-users') > -1 ||
      nextURL.indexOf('manage-internal-users') > -1 || nextURL === '/'
      || this.scollToHide.indexOf(nextURL)) {
      this.applyBodyScrollForUrl(nextURL);
    } else {
      this.ishideScrollState = true;
      if (!this.platform.TRIDENT) {
        document.querySelector('body').classList.remove('hideBodyScroll');
      }
    }
  }

  private applyBodyScrollForUrl(nextURL: string) {
    if (nextURL.slice(nextURL.lastIndexOf('/') + 1) !== 'resultashtml') {
      this.ishideScrollState = false;
      if (!this.platform.TRIDENT) {
        document.querySelector('body').classList.add('hideBodyScroll');
      }
      if (window.location.pathname.includes('confirm-order', 0)) {
        document.querySelector('body').classList.remove('hideBodyScroll');
      }
    } else {
      this.ishideScrollState = true;
      if (!this.platform.TRIDENT) {
        document.querySelector('body').classList.remove('hideBodyScroll');
      }
    }
  }

  navigationFunc(URL: string) {
    let permission = null;
    if (URL.includes('results')) {
      permission = Constants.RESULTS_TOOL_BAR_TAB;
    } else if (URL.includes('specimen-type') || URL.includes('tests') || URL.includes('test-panels') || URL.includes('questions') || URL.includes('payors')) {
      permission = Constants.SUBTAB_MANAGE_LABCONFIG;
    } else if (URL.includes('order')) {
      permission = Constants.SUB_TAB_ORDER;
    } else {
      permission = Constants.MANAGE_USER_TAB;
    }
    const authorizedControlList = JSON.parse(this.localStorageService.getItem(Constants.AUTHORIZED_CONTROLS));
    if (authorizedControlList === null || permission === null) {
      return true;
    }
    const claims = this.authenticationService.GetClaims();
    const extensionPermissions = this.localStorageService.GetClaimsTransformed(claims.extension_permissions)
    .map((item: string) => item.trim());
    if (this.landingService.isControlAuthorized(authorizedControlList, permission, extensionPermissions)) {
      return true;
    } else {
      this.router.navigate(['/']);
    }
  }

  ngOnInit() {
    window.addEventListener('storage', (event) => {
      if (event.storageArea === localStorage) {
        const claims = this.authenticationService.GetClaims();
        if ((claims === undefined || claims === null) && (this.router.url !== '/terms-of-use' && this.router.url !== '/contact-support' && this.router.url !== '/privacy-policy')) {
          this.router.navigate(['account'], { replaceUrl: true });
        }
      }
    });

  }

  toggleSidenav(sidenav: MatSidenav): Promise<MatDrawerToggleResult> {
    return sidenav.toggle();
  }
  close() {
    this.isSidenavOpened = false;
  }

  private showAnnoncementModal() {
    this.broadcastService.setIdbasicToken(environment.idmBasicAuthToken);
  }
}
